import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useModels } from '@hooks/useModels';
import { useLanguage } from '@contexts/LanguageContext';
import { useCollectionState } from './hooks/useCollectionState';
import { getModelFilters } from './config/modelFilters';
import { createFilterDataObject, createFilterLoadingObject } from './utils/filterDataHelpers';
import {
    CollectionHeader,
    CollectionControls,
    CollectionResults
} from './components';
import './Collection.scss';

export const Collection = ({
    modelName,
    models: externalModels = null,
    displayType = 'grid',
    displayOptions = {},
    queryParams = null,
    modelId = null,
    title,
    description,
    enableSearch = false,
    enablePagination = true,
    resourcesPerPageOptions = null, // Will use model-specific defaults if not provided
    default_order_by = null,
    onModelClick = null,
    className = '',
    customHeader = null,
    showViewAll = false,
    pageSize = null,
    endpoint = 'getAll',
    filterOptions = null, // Will use model-specific defaults if not provided
    actions = null,
    onRefresh = null,
    showHorizontalCategoryFilter = false  // New prop for horizontal category filter
}) => {
    const { t } = useLanguage();

    // Use state management hook
    const {
        searchTerm,
        debouncedSearchTerm,
        currentPage,
        resourcesPerPage,
        order_by,
        customFilterValues,
        hasActiveFilters,
        pageSizeOptions,
        handleSearchChange,
        handlePageChange,
        handleResourcesPerPageChange,
        handleOrderByChange,
        handleCustomFilterChange,
        clearFilters,
        clearSearch,
        handleRefresh,
        getCurrentParams,
        baseApiParams
    } = useCollectionState({
        modelName,
        queryParams,
        modelId,
        default_order_by,
        pageSize,
        onRefresh,
        endpoint,
    });

    // Get model-specific filters if not provided
    const effectiveFilterOptions = useMemo(() => {
        if (filterOptions) return filterOptions;
        return getModelFilters(modelName, t);
    }, [filterOptions, modelName, t]);

    // Use provided page size options or model-specific ones
    const effectivePageSizeOptions = resourcesPerPageOptions || pageSizeOptions;

    // Fetch data using useModels hook
    const { models: fetchedModels, loading, error, refetch, pagination } = useModels(
        externalModels ? null : modelName,
        { ...baseApiParams, modelId },
    );

    // Use external models if provided, otherwise use fetched models
    const models = externalModels || fetchedModels;

    // Fetch additional data for filters if needed
    const additionalDataForFilters = useMemo(() => {
        const dataNeeds = {};

        if (effectiveFilterOptions) {
            effectiveFilterOptions.forEach(filter => {
                // Special handling for common filter patterns
                if (filter.key === 'category_ids' && (!filter.options || filter.options.length === 0)) {
                    dataNeeds.categories = 'categories';
                }
                if (filter.key === 'location_id' && (!filter.options || filter.options.length === 0)) {
                    dataNeeds.locations = 'locations';
                }
                if (filter.key === 'tag_ids' && (!filter.options || filter.options.length === 0)) {
                    dataNeeds.tags = 'tags';
                }
                if (filter.key === 'resource_id' && (!filter.options || filter.options.length === 0)) {
                    dataNeeds.resources = 'resources';
                }
            });
        }

        return dataNeeds;
    }, [effectiveFilterOptions]);

    // Fetch additional data using useModels
    const { models: categories, loading: categoriesLoading, refetch: refetchCategories } = useModels(
        additionalDataForFilters.categories ? 'categories' : null,
    );
    const { models: locations, loading: locationsLoading, refetch: refetchLocations } = useModels(
        additionalDataForFilters.locations ? 'locations' : null
    );
    const { models: tags, loading: tagsLoading, refetch: refetchTags } = useModels(
        additionalDataForFilters.tags ? 'tags' : null
    );

    // Trigger initial fetch for filter data
    useEffect(() => {
        if (additionalDataForFilters.categories) {
            refetchCategories({ page_size: -1 });
        }
        if (additionalDataForFilters.locations) {
            refetchLocations({ page_size: -1 });
        }
        if (additionalDataForFilters.tags) {
            refetchTags({ page_size: -1});
        }

    }, [additionalDataForFilters, refetchCategories, refetchLocations, refetchTags]);

    // Auto-refresh when parameters change
    useEffect(() => {
        if (!externalModels && refetch) {
            refetch(getCurrentParams(), modelId);
        }
    }, [getCurrentParams, externalModels, refetch, modelId]);

    // Handle refresh with current params
    const handleCollectionRefresh = useCallback(() => {
        const params = handleRefresh();
        if (!externalModels && refetch) {
            refetch(params, modelId);
        }
    }, [handleRefresh, externalModels, refetch, modelId]);

    return (
        <div className={`collection ${className}`}>
            <CollectionHeader
                title={title}
                description={description}
                modelName={modelName}
                customHeader={customHeader}
                showViewAll={showViewAll}
            />

            {enablePagination && (
                <CollectionControls
                    enableSearch={enableSearch}
                    searchTerm={searchTerm}
                    onSearchChange={handleSearchChange}
                    onClearSearch={clearSearch}
                    filterOptions={effectiveFilterOptions}
                    customFilterValues={customFilterValues}
                    onCustomFilterChange={handleCustomFilterChange}
                    filterData={createFilterDataObject({
                        categories,
                        locations,
                        tags,
                    })}
                    filterDataLoading={createFilterLoadingObject({
                        categoriesLoading,
                        locationsLoading,
                        tagsLoading,
                    })}
                    showHorizontalCategoryFilter={showHorizontalCategoryFilter}
                />
            )}

            <CollectionResults
                models={models}
                pagination={pagination}
                loading={loading}
                error={error}
                hasActiveFilters={hasActiveFilters}
                displayType={displayType}
                displayOptions={displayOptions}
                actions={actions}
                onModelClick={onModelClick}
                enablePagination={enablePagination}
                externalModels={externalModels}
                currentPage={currentPage}
                resourcesPerPage={resourcesPerPage}
                order_by={order_by}
                pageSizeOptions={effectivePageSizeOptions}
                onPageChange={handlePageChange}
                onResourcesPerPageChange={handleResourcesPerPageChange}
                onRefresh={handleCollectionRefresh}
            />
        </div>
    );
};

export default Collection;